import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Title,
    Text,
    Paper,
    Button,
    Alert,
    Table,
    Checkbox,
    List,
    Group,
} from '@mantine/core';
import { IconExternalLink, IconArrowLeft, IconPlayerPlay } from '@tabler/icons-react';
import { entityService } from '../../services/entityServiceSingleton';
import { getScimConfig } from '../../utils/envUtils';
import { extractPluginFunctionListItems, getPluginFunctionPivotApiPath } from '../../utils/pluginFunctionUtils';
import { DynamicTableView } from '../common/DynamicTableView';
import { LoadingState } from '../common/LoadingState';
import { isAbortError } from '../../utils/abortUtils';
import { PrepareInvokeView } from './PrepareInvokeView';
import type { TableColumn } from '../common/GenericTable';

interface PluginFunctionItem {
    Id: string;
    DisplayName?: string;
    Name?: string;
    [key: string]: any;
}

export interface PluginFunctionRunnerProps {
    pluginFunctionIdFromRoute?: string | null;
    pivotEntityIdFromRoute?: string | null;
    pivotDetailIdFromRoute?: string | null;
    prepareCatalogIdFromRoute?: string | null;
    prepareDetailIdFromRoute?: string | null;
    basePath?: string;
}

export const PluginFunctionRunner: React.FC<PluginFunctionRunnerProps> = ({
    pluginFunctionIdFromRoute = null,
    pivotEntityIdFromRoute = null,
    pivotDetailIdFromRoute = null,
    prepareCatalogIdFromRoute = null,
    prepareDetailIdFromRoute = null,
    basePath = 'utilities/pluginfunctions'
}) => {
    const [list, setList] = useState<PluginFunctionItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [listError, setListError] = useState<string | null>(null);
    const [pivotResult, setPivotResult] = useState<any>(null);
    const [isPivotEntityLoading, setIsPivotEntityLoading] = useState(false);
    const [pivotEntityError, setPivotEntityError] = useState<string | null>(null);
    const [pivotWithEntityResult, setPivotWithEntityResult] = useState<any>(null);
    const [entityResultPage, setEntityResultPage] = useState(1);
    const [entityResultSearchQuery, setEntityResultSearchQuery] = useState('');
    const [entityResultSearchAttributes, setEntityResultSearchAttributes] = useState<string[]>(['DisplayName', 'Name', 'Id']);
    const [entityResultOmitSearchDialectExpression, setEntityResultOmitSearchDialectExpression] = useState(false);
    const [entityResultRefreshKey, setEntityResultRefreshKey] = useState(0);
    const entityResultPageSize = 25;
    const [pivotDetailsResult, setPivotDetailsResult] = useState<any>(null);
    const [isPivotDetailsLoading, setIsPivotDetailsLoading] = useState(false);
    const [pivotDetailsError, setPivotDetailsError] = useState<string | null>(null);

    const pivotEntities = useMemo((): { id: string; label: string; entityType?: string; raw: any }[] => {
        if (pivotResult == null) return [];
        const arr = extractPluginFunctionListItems(pivotResult);
        return arr.map((item: any) => {
            const id = String(item?.Id ?? item?.id ?? '').trim();
            const name = item?.DisplayName ?? item?.Name ?? item?.displayName ?? item?.name ?? '';
            const entityType = item?.EntityType ?? item?.entityType ?? item?.Type ?? item?.type ?? '';
            const namePart = name ? String(name).trim() : '';
            const typePart = entityType ? ` (${String(entityType).trim()})` : '';
            const label = namePart ? `${namePart}${typePart} — ${id}` : id || '—';
            return { id, label, entityType: entityType || undefined, raw: item };
        }).filter((x) => x.id);
    }, [pivotResult]);

    const hasEntityTypeColumn = pivotEntities.some((e) => e.entityType != null && e.entityType !== '');

    const entityResultTable = useMemo(() => {
        const data = pivotWithEntityResult;
        if (data == null) return { rows: [] as any[], columns: [] as string[] };
        const arr = extractPluginFunctionListItems(data);
        if (arr.length === 0) return { rows: [], columns: [] };
        const allKeys = new Set<string>();
        arr.forEach((row: any) => {
            if (row && typeof row === 'object') Object.keys(row).forEach((k) => allKeys.add(k));
        });
        const preferredFirst = ['DisplayName', 'displayName', 'Name', 'name', 'EntityType', 'entityType'];
        const idKeys = ['Id', 'id'];
        const rest = [...allKeys].filter((k) => !preferredFirst.includes(k) && !idKeys.includes(k)).sort((a, b) => a.localeCompare(b));
        const idCols = idKeys.filter((k) => allKeys.has(k));
        const columns = [...preferredFirst.filter((k) => allKeys.has(k)), ...rest, ...idCols];
        return { rows: arr, columns };
    }, [pivotWithEntityResult]);

    const [entityResultDynamicColumns, setEntityResultDynamicColumns] = useState<string[]>([]);

    const entityResultListResponse = useMemo(() => {
        const data = pivotWithEntityResult;
        if (data == null || typeof data !== 'object') return null;
        const items = extractPluginFunctionListItems(data);
        const totalResults = data.totalResults ?? items.length;
        const itemsPerPage = data.itemsPerPage ?? entityResultPageSize;
        const startIndex = data.startIndex ?? ((entityResultPage - 1) * entityResultPageSize) + 1;
        const totalPages = Math.max(1, Math.ceil(totalResults / itemsPerPage));
        const currentPage = data.startIndex != null
            ? Math.max(1, Math.min(Math.floor((data.startIndex - 1) / itemsPerPage) + 1, totalPages))
            : Math.min(entityResultPage, totalPages);
        const hasNextPage = currentPage < totalPages;
        const hasPreviousPage = currentPage > 1;
        const listLevel$ = data && typeof data === 'object'
            ? Object.fromEntries(Object.entries(data).filter(([k]) => k.startsWith('$')))
            : {};
        const listLevel_ = data && typeof data === 'object'
            ? Object.fromEntries(Object.entries(data).filter(([k]) => k.startsWith('_')))
            : {};
        return {
            items,
            totalResults,
            currentPage,
            totalPages,
            hasNextPage,
            hasPreviousPage,
            itemsPerPage,
            startIndex,
            ...listLevel$,
            ...listLevel_
        };
    }, [pivotWithEntityResult, entityResultPage, entityResultPageSize]);

    const entityResultColumns = useMemo((): TableColumn<any>[] => {
        const cols = entityResultDynamicColumns.length > 0 ? entityResultDynamicColumns : entityResultTable.columns;
        if (cols.length === 0) return [{ key: '_empty', label: 'Result', render: () => '—' }];
        return cols.map((col) => ({
            key: col,
            label: col,
            sortable: false,
            render: (item: any) => {
                const v = item[col];
                if (v == null) return '—';
                if (typeof v === 'object') return <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>{JSON.stringify(v)}</pre>;
                return String(v);
            }
        }));
    }, [entityResultTable.columns]);

    const pivotListItems = useMemo(() => {
        const data = pivotResult;
        if (data == null || typeof data !== 'object') return [];
        return extractPluginFunctionListItems(data);
    }, [pivotResult]);

    const pivotListColumns = useMemo((): TableColumn<any>[] => {
        const cols: TableColumn<any>[] = [];
        if (hasEntityTypeColumn) {
            cols.push({
                key: 'EntityType',
                label: 'Entity type',
                sortable: false,
                render: (item: any) => item?.EntityType ?? item?.entityType ?? item?.Type ?? item?.type ?? '—',
            });
        }
        cols.push(
            {
                key: 'Display',
                label: 'Display',
                sortable: false,
                render: (item: any) => {
                    const n = item?.DisplayName ?? item?.Name ?? item?.displayName ?? item?.name ?? '';
                    return n ? String(n) : '—';
                },
            },
            {
                key: 'Id',
                label: 'Id',
                sortable: false,
                render: (item: any) => {
                    const id = item?.Id ?? item?.id ?? '';
                    return id ? <Text component="span" ff="monospace">{String(id)}</Text> : '—';
                },
            }
        );
        return cols;
    }, [hasEntityTypeColumn]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setListError(null);
        entityService
            .getEntitiesByType('PluginFunction', 1, 200)
            .then((res) => {
                const items = res?.items ?? res?.data ?? [];
                if (!cancelled) {
                    setList(Array.isArray(items) ? items : []);
                }
            })
            .catch((err: any) => {
                if (!cancelled) {
                    setListError(err?.response?.data?.Message ?? err?.message ?? 'Failed to load plug-in functions.');
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [entityService]);

    /** Reset cached root $pivot payload when the route’s plug-in / pivot context changes (table reloads via `loadData`). */
    useEffect(() => {
        setPivotResult(null);
    }, [pluginFunctionIdFromRoute, pivotEntityIdFromRoute]);

    const entityResultKeyRef = useRef<string>('');

    useEffect(() => {
        if (!pluginFunctionIdFromRoute?.trim() || !pivotEntityIdFromRoute?.trim()) {
            setPivotWithEntityResult(null);
            setPivotEntityError(null);
            entityResultKeyRef.current = '';
            return;
        }
        if (pivotDetailIdFromRoute?.trim()) return;
        const entityKey = `${pluginFunctionIdFromRoute}/${pivotEntityIdFromRoute}`;
        const entityChanged = entityResultKeyRef.current !== entityKey;
        if (entityChanged) {
            entityResultKeyRef.current = entityKey;
            setEntityResultPage(1);
            setEntityResultSearchQuery('');
            setEntityResultSearchAttributes(['DisplayName', 'Name', 'Id']);
        }
        const pageToFetch = entityChanged ? 1 : entityResultPage;
        const searchAttrs = entityResultSearchAttributes?.length ? entityResultSearchAttributes.join(',') : undefined;
        let cancelled = false;
        const ac = new AbortController();
        setIsPivotEntityLoading(true);
        setPivotEntityError(null);
        setPivotWithEntityResult(null);
        entityService
            .callPluginFunctionPivotWithEntity(
                pluginFunctionIdFromRoute,
                pivotEntityIdFromRoute,
                pageToFetch,
                entityResultPageSize,
                undefined,
                undefined,
                entityResultSearchQuery || undefined,
                searchAttrs,
                entityResultOmitSearchDialectExpression,
                ac.signal
            )
            .then((data) => {
                if (!cancelled) {
                    setPivotWithEntityResult(data);
                    // Update dynamic columns from the returned page so the table can render immediately.
                    const rows = extractPluginFunctionListItems(data);
                    const keys = new Set<string>();
                    rows.forEach((row: any) => {
                        if (row && typeof row === 'object') Object.keys(row).forEach((k) => keys.add(k));
                    });
                    const preferredFirst = ['DisplayName', 'displayName', 'Name', 'name', 'EntityType', 'entityType'];
                    const idKeys = ['Id', 'id'];
                    const rest = [...keys].filter((k) => !preferredFirst.includes(k) && !idKeys.includes(k)).sort((a, b) => a.localeCompare(b));
                    const idCols = idKeys.filter((k) => keys.has(k));
                    const columns = [...preferredFirst.filter((k) => keys.has(k)), ...rest, ...idCols];
                    if (columns.length > 0) setEntityResultDynamicColumns(columns);
                }
            })
            .catch((err: any) => {
                if (isAbortError(err, ac.signal)) return;
                if (!cancelled) {
                    setPivotEntityError(err?.response?.data?.Message ?? err?.message ?? 'Failed to load $pivot entity result.');
                }
            })
            .finally(() => {
                if (!cancelled) setIsPivotEntityLoading(false);
            });
        return () => {
            cancelled = true;
            ac.abort();
        };
    }, [entityService, pluginFunctionIdFromRoute, pivotEntityIdFromRoute, pivotDetailIdFromRoute, entityResultPage, entityResultPageSize, entityResultSearchQuery, entityResultSearchAttributes, entityResultOmitSearchDialectExpression, entityResultRefreshKey]);

    useEffect(() => {
        if (!pluginFunctionIdFromRoute?.trim() || !pivotEntityIdFromRoute?.trim() || !pivotDetailIdFromRoute?.trim()) {
            setPivotDetailsResult(null);
            setPivotDetailsError(null);
            return;
        }
        let cancelled = false;
        const ac = new AbortController();
        setIsPivotDetailsLoading(true);
        setPivotDetailsError(null);
        setPivotDetailsResult(null);
        entityService
            .callPluginFunctionPivotDetails(pluginFunctionIdFromRoute, pivotEntityIdFromRoute, pivotDetailIdFromRoute, ac.signal)
            .then((data) => {
                if (!cancelled) setPivotDetailsResult(data);
            })
            .catch((err: any) => {
                if (isAbortError(err, ac.signal)) return;
                if (!cancelled) {
                    setPivotDetailsError(err?.response?.data?.Message ?? err?.message ?? 'Failed to load $details.');
                }
            })
            .finally(() => {
                if (!cancelled) setIsPivotDetailsLoading(false);
            });
        return () => {
            cancelled = true;
            ac.abort();
        };
    }, [entityService, pluginFunctionIdFromRoute, pivotEntityIdFromRoute, pivotDetailIdFromRoute]);

    const displayLabel = (item: PluginFunctionItem) => {
        const id = item.Id ?? item.id ?? '';
        const name = item.DisplayName ?? item.Name ?? item.displayName ?? item.name ?? '';
        return name ? `${name} (${id})` : id;
    };

    const currentPluginCatalogId = useMemo(() => {
        if (!pluginFunctionIdFromRoute?.trim() || !list?.length) return null;
        const item = list.find((p) => (p.Id ?? p.id) === pluginFunctionIdFromRoute);
        const id = item?.CatalogId ?? item?.catalogId;
        return id && String(id).trim() ? String(id).trim() : null;
    }, [list, pluginFunctionIdFromRoute]);

    const isListPage = Boolean(pluginFunctionIdFromRoute?.trim() && !pivotEntityIdFromRoute?.trim());
    const isEntityResultPage = Boolean(pluginFunctionIdFromRoute?.trim() && pivotEntityIdFromRoute?.trim());
    const isDetailPage = Boolean(pluginFunctionIdFromRoute?.trim() && pivotEntityIdFromRoute?.trim() && pivotDetailIdFromRoute?.trim());

    const pivotFullUrlForPage = useMemo(() => {
        if (!pluginFunctionIdFromRoute) return '';
        const path = getPluginFunctionPivotApiPath(pluginFunctionIdFromRoute);
        const base = getScimConfig().apiUrl.replace(/\/$/, '');
        return `${base}${path}`;
    }, [pluginFunctionIdFromRoute]);

    if (prepareCatalogIdFromRoute?.trim()) {
        return (
            <Box p="lg">
                <Title order={5} mb="xs">
                    Plug-in Functions
                </Title>
                <PrepareInvokeView
                    catalogId={prepareCatalogIdFromRoute}
                    basePath={basePath}
                    entityService={entityService}
                    {...(pluginFunctionIdFromRoute && pivotEntityIdFromRoute
                        ? {
                            pluginFunctionId: pluginFunctionIdFromRoute,
                            pivotEntityId: pivotEntityIdFromRoute,
                            invokeTargetId: prepareDetailIdFromRoute ?? undefined,
                        }
                        : {})}
                />
            </Box>
        );
    }

    return (
        <Box p="lg">
            <Title order={5} mb="xs">
                Plug-in Functions
            </Title>

            {isEntityResultPage ? (
                <>
                    <Group gap="xs" mb="md" wrap="wrap">
                        <Button
                            size="xs"
                            leftSection={<IconArrowLeft size={16} />}
                            variant="subtle"
                            onClick={() => {
                                window.location.hash = isDetailPage
                                    ? `${basePath}/${encodeURIComponent(pluginFunctionIdFromRoute!)}/${encodeURIComponent(pivotEntityIdFromRoute!)}`
                                    : `${basePath}/${encodeURIComponent(pluginFunctionIdFromRoute!)}`;
                            }}
                        >
                            {isDetailPage ? 'Back to table' : 'Back to list'}
                        </Button>
                        {isDetailPage && currentPluginCatalogId && (
                            <Button
                                size="xs"
                                variant="outline"
                                leftSection={<IconPlayerPlay size={16} />}
                                onClick={() => {
                                    window.location.hash = `${basePath}/prepare/${encodeURIComponent(pluginFunctionIdFromRoute!)}/${encodeURIComponent(pivotEntityIdFromRoute!)}/${encodeURIComponent(pivotDetailIdFromRoute!)}/${encodeURIComponent(currentPluginCatalogId)}`;
                                }}
                            >
                                Prepare invoke
                            </Button>
                        )}
                    </Group>
                    {isDetailPage ? (
                        <>
                            <Text size="sm" c="dimmed" mb="md">
                                $pivot/&#123;entityId&#125;/$details/&#123;detailId&#125; for plug-in <code>{pluginFunctionIdFromRoute}</code>, entity <code>{pivotEntityIdFromRoute}</code>, detail <code>{pivotDetailIdFromRoute}</code>.
                            </Text>
                            {pivotDetailsError && (
                                <Alert color="red" mb="md" withCloseButton onClose={() => setPivotDetailsError(null)}>
                                    {pivotDetailsError}
                                </Alert>
                            )}
                            {isPivotDetailsLoading ? (
                                <LoadingState variant="centered" label="Loading $details…" size={24} />
                            ) : pivotDetailsResult != null ? (
                                <>
                                    {(pivotDetailsResult.StatusCode != null || pivotDetailsResult.Message != null) && (
                                        <Alert
                                            color={pivotDetailsResult.StatusCode === 'OK' ? 'green' : 'blue'}
                                            mb="md"
                                        >
                                            {pivotDetailsResult.StatusCode != null && <strong>{pivotDetailsResult.StatusCode}</strong>}
                                            {pivotDetailsResult.StatusCode != null && pivotDetailsResult.Message != null && ' — '}
                                            {pivotDetailsResult.Message != null && pivotDetailsResult.Message}
                                        </Alert>
                                    )}
                                    {(() => {
                                    const payload = pivotDetailsResult?.Result ?? pivotDetailsResult;
                                    const arr = extractPluginFunctionListItems(payload);
                                    const isArrayLike = arr.length > 0;
                                    const isSingleObject = !isArrayLike && payload && typeof payload === 'object' && !Array.isArray(payload);
                                    const singleObj = isSingleObject ? payload as Record<string, any> : null;
                                    const preferredFirst = ['DisplayName', 'displayName', 'Name', 'name', 'EntityType', 'entityType'];
                                    const idKeys = ['Id', 'id'];
                                    if (isSingleObject && singleObj) {
                                        const allKeys = Object.keys(singleObj);
                                        const rest = allKeys.filter((k) => !preferredFirst.includes(k) && !idKeys.includes(k)).sort((a, b) => a.localeCompare(b));
                                        const detailKeys = [...preferredFirst.filter((k) => allKeys.includes(k)), ...rest, ...idKeys.filter((k) => allKeys.includes(k))];
                                        return (
                                            <Paper withBorder p="md" maw={800}>
                                                <Table>
                                                    <Table.Thead>
                                                        <Table.Tr>
                                                            <Table.Th fw={600} w={200}>Property</Table.Th>
                                                            <Table.Th>Value</Table.Th>
                                                        </Table.Tr>
                                                    </Table.Thead>
                                                    <Table.Tbody>
                                                        {detailKeys.map((key) => {
                                                            const v = singleObj[key];
                                                            return (
                                                                <Table.Tr key={key}>
                                                                    <Table.Td ff="monospace" fw={500} style={{ verticalAlign: 'top' }}>{key}</Table.Td>
                                                                    <Table.Td>
                                                                        {typeof v === 'boolean' ? (
                                                                            <Checkbox checked={v} readOnly size="sm" />
                                                                        ) : v == null ? (
                                                                            '—'
                                                                        ) : typeof v === 'object' ? (
                                                                            <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(v, null, 2)}</pre>
                                                                        ) : (
                                                                            String(v)
                                                                        )}
                                                                    </Table.Td>
                                                                </Table.Tr>
                                                            );
                                                        })}
                                                    </Table.Tbody>
                                                </Table>
                                            </Paper>
                                        );
                                    }
                                    const allKeys = new Set<string>();
                                    if (isArrayLike) {
                                        (arr as any[]).forEach((row: any) => {
                                            if (row && typeof row === 'object') Object.keys(row).forEach((k) => allKeys.add(k));
                                        });
                                    }
                                    const rest = [...allKeys].filter((k) => !preferredFirst.includes(k) && !idKeys.includes(k)).sort((a, b) => a.localeCompare(b));
                                    const idCols = idKeys.filter((k) => allKeys.has(k));
                                    const detailColumns = [...preferredFirst.filter((k) => allKeys.has(k)), ...rest, ...idCols];
                                    return (
                                        <>
                                            {isArrayLike ? (
                                                <Paper withBorder mah="70vh" style={{ overflow: 'auto' }}>
                                                    <Table>
                                                        <Table.Thead>
                                                            <Table.Tr>
                                                                {detailColumns.map((col) => (
                                                                    <Table.Th key={col}>{col}</Table.Th>
                                                                ))}
                                                            </Table.Tr>
                                                        </Table.Thead>
                                                        <Table.Tbody>
                                                            {(arr as any[]).map((row: any, idx: number) => (
                                                                <Table.Tr key={row?.Id ?? row?.id ?? idx}>
                                                                    {detailColumns.map((col) => {
                                                                        const v = row[col];
                                                                        const display = v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v);
                                                                        return (
                                                                            <Table.Td key={col} style={col === 'Id' || col === 'id' ? { fontFamily: 'monospace' } : undefined}>
                                                                                {display}
                                                                            </Table.Td>
                                                                        );
                                                                    })}
                                                                </Table.Tr>
                                                            ))}
                                                        </Table.Tbody>
                                                    </Table>
                                                </Paper>
                                            ) : (
                                                <Paper withBorder p="md" bg="var(--mantine-color-default-hover)" mah="70vh" style={{ overflow: 'auto' }}>
                                                    <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                        {JSON.stringify(pivotDetailsResult, null, 2)}
                                                    </pre>
                                                </Paper>
                                            )}
                                        </>
                                    );
                                    })()}
                                </>
                            ) : null}
                        </>
                    ) : (
                        <>
                            <Text size="sm" c="dimmed" mb="md">
                                $pivot/&#123;entityId&#125; result for plug-in <code>{pluginFunctionIdFromRoute}</code>, entity <code>{pivotEntityIdFromRoute}</code>. Click a row to open $details.
                            </Text>
                            <DynamicTableView
                                entityType="entity"
                                authResolved={true}
                                hideHeader={true}
                                hideToolbarActions={true}
                                tabsMode="tableOnly"
                                loadData={async (page, abortSignal, searchFilter, _selectedType, sortBy, searchtext, searchAttributes, omitSearchDialectExpression) => {
                                    const data = await entityService.callPluginFunctionPivotWithEntity(
                                        pluginFunctionIdFromRoute!,
                                        pivotEntityIdFromRoute!,
                                        page,
                                        entityResultPageSize,
                                        sortBy,
                                        searchFilter,
                                        searchtext,
                                        searchAttributes,
                                        omitSearchDialectExpression,
                                        abortSignal
                                    );
                                    const rows = extractPluginFunctionListItems(data);
                                    // Update dynamic columns based on this page.
                                    const keys = new Set<string>();
                                    rows.forEach((row: any) => {
                                        if (row && typeof row === 'object') Object.keys(row).forEach((k) => keys.add(k));
                                    });
                                    const preferredFirst = ['DisplayName', 'displayName', 'Name', 'name', 'EntityType', 'entityType'];
                                    const idKeys = ['Id', 'id'];
                                    const rest = [...keys].filter((k) => !preferredFirst.includes(k) && !idKeys.includes(k)).sort((a, b) => a.localeCompare(b));
                                    const idCols = idKeys.filter((k) => keys.has(k));
                                    const cols = [...preferredFirst.filter((k) => keys.has(k)), ...rest, ...idCols];
                                    if (cols.length > 0) setEntityResultDynamicColumns(cols);

                                    const totalResults = Number((data as any)?.totalResults ?? rows.length ?? 0) || 0;
                                    const totalPages = Math.max(1, Math.ceil(totalResults / entityResultPageSize));
                                    return {
                                        data: rows,
                                        totalResults,
                                        currentPage: page,
                                        totalPages,
                                        hasNextPage: page < totalPages,
                                        hasPreviousPage: page > 1
                                    };
                                }}
                                generateColumns={() => entityResultColumns}
                                onEntitySelect={(row: any) => {
                                    const id = row?.Id ?? row?.id;
                                    if (id != null && id !== '') {
                                        window.location.hash = `${basePath}/${encodeURIComponent(pluginFunctionIdFromRoute!)}/${encodeURIComponent(pivotEntityIdFromRoute!)}/${encodeURIComponent(String(id))}`;
                                    }
                                }}
                            />
                        </>
                    )}
                </>
            ) : isListPage ? (
                <>
                    <Group gap="xs" mb="md" wrap="wrap">
                        <Button
                            size="xs"
                            leftSection={<IconArrowLeft size={16} />}
                            variant="subtle"
                            onClick={() => { window.location.hash = basePath; }}
                        >
                            Back to list
                        </Button>
                        {pivotFullUrlForPage && (
                            <Button
                                size="xs"
                                variant="outline"
                                leftSection={<IconExternalLink size={16} />}
                                onClick={() => window.open(pivotFullUrlForPage, '_blank', 'noopener,noreferrer')}
                            >
                                Open list endpoint in new tab
                            </Button>
                        )}
                    </Group>
                    <Text size="sm" c="dimmed" mb="md">
                        $pivot results for plug-in function <code>{pluginFunctionIdFromRoute}</code>. Select an entity to open <code>.../$pivot/&#123;entityId&#125;</code>. Search and filter use the same query parameters as other SCIM list endpoints; sort and paging apply to the rows returned.
                    </Text>
                    <DynamicTableView
                        entityType="entity"
                        authResolved={true}
                        hideHeader={true}
                        hideToolbarActions={true}
                        tabsMode="tableOnly"
                        loadData={async (page, abortSignal, searchFilter, _selectedType, sortBy, searchtext, searchAttributes, omitSearchDialectExpression) => {
                            const data = await entityService.callPluginFunctionPivot(pluginFunctionIdFromRoute!, abortSignal, {
                                filter: searchFilter || undefined,
                                searchtext: searchtext || undefined,
                                searchAttributes: searchAttributes || undefined,
                                omitSearchDialectExpression,
                            });
                            setPivotResult(data);
                            let rows = extractPluginFunctionListItems(data);
                            if (sortBy) {
                                const [colRaw, dirRaw] = sortBy.split(':');
                                const col = (colRaw ?? '').trim();
                                const dir = (dirRaw ?? 'ascending').toLowerCase();
                                if (col) {
                                    rows = [...rows].sort((a: any, b: any) => {
                                        const av = a?.[col];
                                        const bv = b?.[col];
                                        const as = av == null ? '' : String(av);
                                        const bs = bv == null ? '' : String(bv);
                                        const cmp = as.localeCompare(bs, undefined, { sensitivity: 'base' });
                                        return dir.startsWith('desc') ? -cmp : cmp;
                                    });
                                }
                            }
                            const pageSize = 25;
                            const envelope = typeof data === 'object' && data !== null && !Array.isArray(data) ? (data as any) : null;
                            const totalResults =
                                envelope != null && typeof envelope.totalResults === 'number'
                                    ? envelope.totalResults
                                    : rows.length;
                            const totalPages = Math.max(1, Math.ceil(totalResults / pageSize));
                            const start = (page - 1) * pageSize;
                            const pageRows = rows.slice(start, start + pageSize);
                            return {
                                data: pageRows,
                                totalResults,
                                currentPage: page,
                                totalPages,
                                hasNextPage: page < totalPages,
                                hasPreviousPage: page > 1,
                            };
                        }}
                        generateColumns={() => pivotListColumns}
                        onEntitySelect={(row: any) => {
                            const id = row?.Id ?? row?.id;
                            if (id != null && id !== '') {
                                window.location.hash = `${basePath}/${encodeURIComponent(pluginFunctionIdFromRoute!)}/${encodeURIComponent(String(id))}`;
                            }
                        }}
                    />
                    {pivotResult != null && pivotListItems.length === 0 && (
                        <Paper withBorder p="md" mt="md" bg="var(--mantine-color-default-hover)" mah={400} style={{ overflow: 'auto' }}>
                            <Text size="sm" c="dimmed" mb="xs">Raw response (no items array):</Text>
                            <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {JSON.stringify(pivotResult, null, 2)}
                            </pre>
                        </Paper>
                    )}
                </>
            ) : (
                <>
                    <Text size="sm" c="dimmed" mb="md">
                        Click a plug-in function to open its <code>$pivot</code> results.
                    </Text>
                    {listError && (
                        <Alert color="red" mb="md" withCloseButton onClose={() => setListError(null)}>
                            {listError}
                        </Alert>
                    )}
                    {loading ? (
                        <LoadingState variant="centered" label="Loading plug-in functions…" size={24} />
                    ) : (
                        <Paper withBorder>
                            <List listStyleType="none">
                                {list.map((item) => {
                                    const id = item.Id ?? item.id ?? '';
                                    if (!id) return null;
                                    const href = `#${basePath}/${encodeURIComponent(id)}`;
                                    return (
                                        <List.Item key={id}>
                                            <Box
                                                component="a"
                                                href={href}
                                                display="block"
                                                py="sm"
                                                px="md"
                                                style={{ textDecoration: 'none', color: 'inherit', borderBottom: '1px solid var(--mantine-color-default-border)' }}
                                            >
                                                <Text fw={500}>{displayLabel(item)}</Text>
                                                <Text size="xs" c="dimmed">{id}</Text>
                                            </Box>
                                        </List.Item>
                                    );
                                })}
                            </List>
                        </Paper>
                    )}
                </>
            )}
        </Box>
    );
};
